import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChartId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FetchError } from '../../../utils/fetch-error';
import { chartEnvelopeKey, useChartEnvelope } from './useChartData';
import { useRecordPermissions } from './useRecordPermissions';

/**
 * The permission layer around "Update this chart", shared by the two surfaces that offer it: the
 * single-chart view and a dashboard card's edit controls.
 *
 * It is one hook rather than two copies because the flow is subtle in three places, and both copies
 * had to get all three right:
 *
 * 1. THE GATE MUST RUN BEFORE THE FAN-OUT CONFIRM. That dialog's main action calls the update
 *    function directly, so a locked chart that reaches it skips the gate and meets a raw 403.
 * 2. The SERVER is the authority, and the gate can be wrong in both directions — it reads a cached
 *    envelope that may predate a permission change, and its role number comes from a different
 *    query than the backend enforces. So a refused write has to land on the same dialog the gate
 *    would have shown, and drop the stale envelope so the next render recomputes from server truth.
 * 3. A refused READ is a stronger answer than "no edit access", and needs its own flag: with no
 *    envelope `isResolved` is false, so a gate keyed only on that would go SILENT rather than
 *    refuse.
 *
 * Only the gate, the refusal recovery and the dialog's state live here. The two callers' SUCCESS
 * paths differ materially — the chart page seeds two caches, tracks Mixpanel and closes its title
 * editor; the card invalidates and clears its working copy — and are deliberately left to them.
 *
 * `chartId` is null for a draft: there is no saved row, so nothing is fetched and nothing is
 * governed. Every flag below then reads false and the gate is inert, which is correct — a draft is
 * the user's own until they first save it.
 */
export const useChartEditGate = (company: string, chartId: ChartId | null) => {
  const queryClient = useQueryClient();
  const { data: envelope, error } = useChartEnvelope(company, chartId);
  const { isResolved, canEdit, userRole } = useRecordPermissions(envelope);
  const [showNoEditAccess, setShowNoEditAccess] = useState(false);

  // The server declined to send this chart at all.
  const isChartRestricted = error instanceof FetchError && error.status === 403;
  // Readable, but not changeable — what the View only badge marks.
  const isViewOnly = isResolved && !canEdit;

  /**
   * The server refused the write. Show the dialog the gate would have shown — the user still needs
   * the copy route, and a raw error modal offers them nothing — and drop the stale envelope so the
   * next render recomputes the gate from server truth and the badge appears.
   *
   * Only the ENVELOPE is invalidated, not the render query. A refused write changed nothing about
   * the chart's data, and this is awaited, so pulling the render query in would hold the dialog
   * behind a `/getData` round trip that accomplishes nothing.
   */
  const handleForbidden = async () => {
    await queryClient.invalidateQueries({ queryKey: chartEnvelopeKey(company, chartId) });
    setShowNoEditAccess(true);
  };

  return {
    envelope,
    // The envelope query's raw error, exposed so a caller can tell a TERMINAL failure that isn't a
    // 403 (a 404 on a deleted chart, say) apart from ordinary loading — `isChartRestricted` alone only
    // covers the one status this gate itself acts on. See SingleChartView's sharing-target derivation.
    error,
    userRole,
    // Whether the gate can answer yet. False while /getChart is in flight and for a draft, which is
    // why every flag below is inert in those states — a caller that must not act on an unanswered
    // gate (a chat-driven Update, say) waits on this rather than reading the flags.
    isResolved,
    isChartRestricted,
    isViewOnly,
    // Refuse a write for either reason. Read this rather than either flag alone: the two surfaces
    // previously spelled their gates differently, and one of them omitted `isChartRestricted`.
    isWriteBlocked: isChartRestricted || isViewOnly,
    showNoEditAccess,
    setShowNoEditAccess,
    handleForbidden
  };
};
